import { useMutation } from '@apollo/client';
import { RefreshOutlined as RefreshIcon } from '@mui/icons-material';
import { Chip, CircularProgress, IconButton, Link, Stack, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { REUPLOAD_TALENT_CV_TO_TRIMOJI } from '@gql/trimoji';

interface ScoringReadinessBadgeProps {
    resumeId?: string;
    resumePath?: string;
    talentId: string;
    talentSlug: string;
    onReuploadSuccess?: () => void;
}

const ScoringReadinessBadge: React.FC<ScoringReadinessBadgeProps> = ({
    resumeId,
    resumePath,
    talentId,
    talentSlug,
    onReuploadSuccess,
}) => {
    const theme = useTheme();
    const { showSnackbar } = useSnackbar();

    const [reUpload, { loading }] = useMutation(REUPLOAD_TALENT_CV_TO_TRIMOJI, {
        onCompleted: (data) => {
            if (data?.reUploadTalentCVToTrimoji?.success) {
                showSnackbar('CV envoyé à l\'analyse. Actualisez dans quelques instants.', 'success');
                onReuploadSuccess?.();
            } else {
                showSnackbar(data?.reUploadTalentCVToTrimoji?.message || 'Erreur lors de l\'envoi du CV.', 'error');
            }
        },
        onError: () => {
            showSnackbar('Erreur lors de l\'envoi du CV à Trimoji.', 'error');
        },
    });

    const handleReanalyze = () => {
        reUpload({ variables: { talentId } });
    };

    // State 1: Ready — CV has been analyzed (resumeId present)
    if (resumeId) {
        return (
            <Chip
                label="Prêt"
                size="small"
                sx={{
                    backgroundColor: theme.palette.successGreen?.main ?? '#4caf50',
                    color: theme.palette.neutralWhite?.main ?? '#fff',
                    fontWeight: 600,
                    fontSize: 11,
                }}
            />
        );
    }

    // State 2: CV exists but not yet analyzed
    if (resumePath) {
        return (
            <Stack direction="row" spacing={0.5} alignItems="center">
                <Chip
                    label="CV non analysé"
                    size="small"
                    sx={{
                        backgroundColor: theme.palette.warningOrange?.main ?? '#ff9800',
                        color: theme.palette.neutralWhite?.main ?? '#fff',
                        fontWeight: 600,
                        fontSize: 11,
                    }}
                />
                <Tooltip title="Analyser le CV">
                    <span>
                        <IconButton
                            size="small"
                            onClick={handleReanalyze}
                            disabled={loading}
                            color="warning"
                            aria-label="Analyser le CV"
                        >
                            {loading ? <CircularProgress size={14} /> : <RefreshIcon fontSize="inherit" />}
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>
        );
    }

    // State 3: No CV at all
    return (
        <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip
                label="CV manquant"
                size="small"
                sx={{
                    backgroundColor: theme.palette.errorRed?.main ?? '#f44336',
                    color: theme.palette.neutralWhite?.main ?? '#fff',
                    fontWeight: 600,
                    fontSize: 11,
                }}
            />
        </Stack>
    );
};

export default ScoringReadinessBadge;
